import { useState, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";
import { Button } from "@shadcn-in-nx/ui/button";
import { Loader2, RotateCw } from "lucide-react";
import DateRangeSelector from "./DateRangeSelector";
import AuditResults from "./AuditResults";

function getDefaultRange() {
    const now = new Date();
    const d = new Date(now);
    d.setDate(d.getDate() - 30);
    return {
        since: d.toISOString().split("T")[0],
        until: now.toISOString().split("T")[0],
    };
}

export default function FacebookAudit() {
    const { t } = useTranslation();
    const [range, setRange] = useState(getDefaultRange);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    const { call: fetchAudit, isLoading } = useBluelagoon({
        method: "getFacebookPixelAudit",
    });

    const load = useCallback(async () => {
        setError(null);
        const res = await fetchAudit(range);
        if (res?.error) {
            setError(res.error);
            setData(null);
        } else if (res?.data) {
            setData(res.data);
        }
    }, [fetchAudit, range]);

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">
                        {t("admin.facebookAudit.title")}
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        {t("admin.facebookAudit.description")}
                    </p>
                </div>
                <Button variant="outline" onClick={load} disabled={isLoading}>
                    {isLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                        <RotateCw className="h-4 w-4 mr-2" />
                    )}
                    {t("admin.facebookAudit.fetch")}
                </Button>
            </div>

            <DateRangeSelector
                since={range.since}
                until={range.until}
                onChange={setRange}
            />

            {error && (
                <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
                    {error}
                </div>
            )}

            {isLoading && !data && (
                <div className="flex justify-center items-center h-32">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            )}

            <AuditResults data={data} />
        </div>
    );
}
